import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import TextButton from 'LuxKit/Button/TextButton'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import React, { useCallback, useContext } from 'react'
import Sheet from 'components/utils/Sheet'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { useAppSelector } from 'hooks/reduxHooks'
import { FormattedMessage } from 'react-intl'

interface Props {
  loggedIn: boolean
}
function TripLandingPageHeader(props: Props) {
  const showModal = useContext(ModalContext)
  const isTripChatPageEnabled = useOptimizelyExperiment(OptimizelyFeatureFlags.trpTripChatPage)
  const deviceType = useAppSelector(state => state.system.device.deviceType)
  const isDesktop = deviceType !== 'mobile' && deviceType !== 'tablet'
  const showAITripPlanner = isTripChatPageEnabled && isDesktop

  const onLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const contentWithFeatureFlag = (
    <Group direction="vertical" gap={16} desktopDirection="horizontal" verticalAlign="center" desktopHorizontalAlign="space-between" desktopGap={24}>
      <Group direction="vertical" gap={16}>
        <Group direction="vertical" gap={8}>
          <StatusLabel
            variant="confirmation"
            startIcon={<SolidSparklesIcon />}
          >
            <FormattedMessage
              id="9exIGc9j05L-Bj2MmdSl"
              defaultMessage="AI Trip Planner"
              description="Label for AI Trip Planner feature in the trip planner feature"
            />
          </StatusLabel>
          <HeadingTextBlock variant="heading1">
            <Heading variant="heading1">
              <FormattedMessage
                id="1HocI63qCH9orwX8Qv9g"
                defaultMessage="The <i>easiest</i> way"
                description="Heading text for easiest way to plan in the trip planner"
                values={{
                  i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                }}
              />
            </Heading>
            <Heading variant="heading1">
              <FormattedMessage
                id="6uHuToDGc1_dzmmmxZaF"
                defaultMessage="to plan a holiday, ever"
                description="Heading text for to plan a holiday ever in the trip planner"
              />
            </Heading>
          </HeadingTextBlock>
          <BodyText variant="large">
            <FormattedMessage
              id="k9SCYGVFzxqbvCs_YbXR"
              defaultMessage="We combine expert travel knowledge with powerful AI{br} to make trip planning effortless, smart and fun."
              description="Hero section body copy text describing the AI-powered trip planning tool on the landing page"
              values={{
                br: <CSSBreakpoint as="br" min="tablet" />,
              }}
            />
          </BodyText>
        </Group>
        <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start">
          <TextButton kind="primary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to="/trip-planner/generate-trip">
            <FormattedMessage
              id="js-m2IDe4z5PTo2WA_BV"
              defaultMessage="Start planning - It’s free"
              description="Button text for starting trip planning in the trip planner feature"
            />
          </TextButton>
          {!props.loggedIn && (
            <BodyText variant="medium" weight="bold">
              <FormattedMessage
                id="JWEedessdSEPHvhbTP8L"
                defaultMessage="Already have trips? <link>Log in</link>"
                description="Text for users who already have trips with login link"
                values={{
                  link: (chunks: React.ReactNode) => <TextLink key="login-link" onClick={onLoginClick}>{chunks}</TextLink>,
                }}
              />
            </BodyText>
          )}
        </Group>
      </Group>
      <Sheet padding="20 16" tabletPadding={24} background="neutral-eight" borderRadius="L" paletteType="default">
        <Group direction="vertical" gap={4}>
          <BodyText variant="medium" weight="bold" colour="highlight-secondary">
            <FormattedMessage
              id="Dv9zYxmqA-avgpMj4dPB"
              defaultMessage="You’ve been invited to try our"
              description="Descriptive text for invitation to try new feature"
            />
          </BodyText>
          <Group direction="horizontal" gap={12}>
            <Heading variant="heading3" colour="neutral-one">
              <FormattedMessage
                id="wc8UNVwrVy-nBlEYV8jl"
                defaultMessage="New AI TripPlanner"
                description="Heading for new AI TripPlanner feature in the trip planner feature"
              />
            </Heading>
            <StatusLabel variant="confirmation">
              <FormattedMessage
                id="WUaRzh5CUAMFiwVWcV9b"
                defaultMessage="Beta"
                description="Badge label indicating that a feature is in beta and still being tested or refined"
              />
            </StatusLabel>
          </Group>
          <Group direction="vertical" gap={8}>
            <BodyText variant="medium">
              <FormattedMessage
                id="bHcmadA5OtnbKJ1bXhvV"
                defaultMessage="Chat with your personal travel designer who will help{br}craft the perfect itinerary for you."
                description="Description of AI TripPlanner chat feature for crafting itineraries"
                values={{ br: <br /> }}
              />
            </BodyText>
            <TextButton kind="primary" size="large" to="/trip-planner/chat-trip-planner">
              <FormattedMessage
                id="nlDun-eHYEwJB-9phRqN"
                defaultMessage="Give it a go"
                description="Button text for trying the chat trip planner in the trip planner"
              />
            </TextButton>
          </Group>
        </Group>
      </Sheet>
    </Group>
  )

  const contentWithoutFeatureFlag = (
    <Group direction="vertical" gap={32} desktopGap={40} desktopDirection="horizontal" desktopHorizontalAlign="space-between">
      <Group direction="vertical" gap={8}>
        <StatusLabel
          variant="confirmation"
          startIcon={<SolidSparklesIcon />}
        >
          <FormattedMessage
            id="9exIGc9j05L-Bj2MmdSl"
            defaultMessage="AI Trip Planner"
            description="Label for AI Trip Planner feature in the trip planner feature"
          />
        </StatusLabel>
        <HeadingTextBlock variant="heading1">
          <Heading variant="heading1">
            <FormattedMessage
              id="1HocI63qCH9orwX8Qv9g"
              defaultMessage="The <i>easiest</i> way"
              description="Heading text for easiest way to plan in the trip planner"
              values={{
                i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
              }}
            />
          </Heading>
          <Heading variant="heading1">
            <FormattedMessage
              id="6uHuToDGc1_dzmmmxZaF"
              defaultMessage="to plan a holiday, ever"
              description="Heading text for to plan a holiday ever in the trip planner"
            />
          </Heading>
        </HeadingTextBlock>
        <BodyText variant="large">
          <FormattedMessage
            id="k9SCYGVFzxqbvCs_YbXR"
            defaultMessage="We combine expert travel knowledge with powerful AI{br} to make trip planning effortless, smart and fun."
            description="Hero section body copy text describing the AI-powered trip planning tool on the landing page"
            values={{
              br: <CSSBreakpoint as="br" min="tablet" />,
            }}
          />
        </BodyText>
      </Group>
      <Group direction="vertical" gap={16} verticalAlign="center" horizontalAlign="start" desktopHorizontalAlign="center">
        <TextButton kind="primary" size="large" startIcon={<SolidSparklesIcon />} shape="round" to="/trip-planner/generate-trip">
          <FormattedMessage
            id="js-m2IDe4z5PTo2WA_BV"
            defaultMessage="Start planning - It’s free"
            description="Button text for starting trip planning in the trip planner feature"
          />
        </TextButton>
        {!props.loggedIn && (
          <BodyText variant="medium" weight="bold">
            <FormattedMessage
              id="JWEedessdSEPHvhbTP8L"
              defaultMessage="Already have trips? <link>Log in</link>"
              description="Text for users who already have trips with login link"
              values={{
                link: (chunks: React.ReactNode) => <TextLink key="login-link" onClick={onLoginClick}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        )}
      </Group>
    </Group>
  )

  return (
    <Sheet
      paletteType="inverse"
      style={{ position: 'relative' }}
    >
      {showAITripPlanner ? contentWithFeatureFlag : contentWithoutFeatureFlag}
    </Sheet>
  )
}

export default TripLandingPageHeader
